import { useEffect, useState } from "react";

import { api } from "../api";
import LessonProgressRing from "../components/LessonProgressRing";
import type { Lesson } from "../types";
import { useMainButton } from "../tg";

type Props = {
  sectionSlug: string;
  sectionTitle: string;
  sectionDescription?: string | null;
  // Кеш из App: если уже есть — рендерим мгновенно, без "Загрузка…".
  // Свежий запрос всё равно полетит в useEffect и обновит и локальный state, и App-кеш.
  initialLessons?: Lesson[];
  onLessonsLoaded: (slug: string, lessons: Lesson[]) => void;
  onOpenLesson: (lesson: Lesson) => void;
};

// Generic-экран списка уроков секции. Используется для всех секций кроме
// parasites (у тех свой флоу с выбором). Стиль карточек переиспользует
// .lesson-card из MyParasites, чтобы visually consistent.
export default function SectionLessons({
  sectionSlug,
  sectionTitle,
  sectionDescription,
  initialLessons,
  onLessonsLoaded,
  onOpenLesson,
}: Props) {
  // Локальный state позволяет рендерить мгновенно из App-кеша (через initialLessons),
  // а fresh fetch обновляет и его, и App-кеш — без визуального дёрга.
  const [lessons, setLessons] = useState<Lesson[] | null>(initialLessons ?? null);
  const [error, setError] = useState<string | null>(null);

  useMainButton(null);

  // Тихий revalidate. App-кеш обновляем всегда, даже если экран успели размонтировать —
  // иначе при быстром «зашёл-вышел-зашёл» кеш не успевает заполниться и второй заход
  // снова показывает «Загрузка…».
  useEffect(() => {
    let cancelled = false;
    api<Lesson[]>(`/sections/${sectionSlug}/lessons`)
      .then((data) => {
        onLessonsLoaded(sectionSlug, data);
        if (cancelled) return;
        setLessons(data);
      })
      .catch((e: Error) => {
        if (!cancelled && !lessons) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
    // Перезапрашиваем при смене секции.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionSlug]);

  // Для завершённого урока CTA-текст не показываем — статус «пройдено»
  // уже передаёт зелёное кольцо с галочкой справа.
  const ctaLabel = (lesson: Lesson): string | null => {
    if (lesson.blocks_total > 0 && lesson.blocks_completed >= lesson.blocks_total) {
      return null;
    }
    if (lesson.blocks_completed > 0) return "Продолжить";
    return "Начать";
  };

  return (
    <div className="screen screen-with-sticky-header">
      <header className="lesson-header-sticky">
        <h1>{sectionTitle}</h1>
        {sectionDescription && <p className="subtitle">{sectionDescription}</p>}
      </header>

      {error && <div className="error-card">{error}</div>}

      {!lessons && !error && <div className="card">Загрузка…</div>}

      {lessons && lessons.length === 0 && (
        <div className="card">В этом разделе пока нет уроков.</div>
      )}

      {lessons && lessons.length > 0 && (
        <div className="lesson-cards">
          {lessons.map((lesson) => {
            const total = lesson.blocks_total;
            const done = lesson.blocks_completed;
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            const isDone = total > 0 && done >= total;
            const isEmpty = done === 0;
            return (
              <button
                key={lesson.id}
                type="button"
                className={`lesson-card lesson-card-with-ring ${isDone ? "lesson-card-done" : ""}`}
                onClick={() => onOpenLesson(lesson)}
              >
                <div className="lesson-card-main">
                  <span className="lesson-card-title">
                    <span className="lesson-card-num">#{lesson.display_order}</span>{" "}
                    {lesson.title}
                  </span>
                  {lesson.description && (
                    <div className="lesson-card-desc">{lesson.description}</div>
                  )}
                  <div className="lesson-card-meta">
                    {done}/{total} блоков
                    {ctaLabel(lesson) && <> · {ctaLabel(lesson)}</>}
                  </div>
                </div>
                <LessonProgressRing
                  total={total}
                  done={done}
                  pct={pct}
                  isDone={isDone}
                  isEmpty={isEmpty}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
